/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { object, shape, string } from 'prop-types';
import { compose } from 'recompose';
import {
    Button, Grid, makeStyles, useMediaQuery,
} from '@material-ui/core';

import withPageView from '../../../../helpers/tracking/hocs/withPageView';
import { trackPage } from '../../../../helpers/tracking/common/commonTrackingHelpers';
import GraphqlSeoHeadTags from '../../../../components/GraphqlComponents/GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import { getBrand, getCustomerEmail } from '../../state/ducks/Common/Common-Selectors';
import { isEmpty } from '../../utils/object';
import EventDispatcher from '../../state/reducers/EventDispatcher';
import configENV from '../../config';
import { CUSTOM_BREAKPOINTS } from '../../style/variables';
import { sortSubscriptions, getSubscriptionStatuses } from './helper';
import AccountHome from '../MyAccount/AccountHome';
import StatusBar from './Components/StatusBar';
import SubscriptionList from './Components/SubscriptionList';
import AccountLeftMenu from '../../common/Desktop/AccountLeftMenu';
import LoadingSkeleton from './Components/LoadingSkeleton';
import { getCurrentBrandId } from '../../utils/featureSelectors';
import brandtheme from '../../../../components/AppShell/brandtheme';

const { PERSIST_KEY } = configENV;
const seo = {
    page_title: 'My Account',
    location: { pathname: '/account/subscriptions' },
};

const styles = makeStyles({
    defaultBody: {
        color: '#666',
        maxWidth: '86%',
        '@media screen and (min-width: 1440px)': {
            maxWidth: '92%',
        },
        '@media screen and (max-width: 920px)': {
            maxWidth: '100%',
        },
    },
    header: {
        padding: '0 0 40px',
        '@media screen and (max-width: 1024px)': {
            padding: '32px 0',
        },
        '& h1': {
            fontSize: '32px !important',
            fontWeight: '700 !important',
            textAlign: 'start',
            margin: '0 !important',
            '@media screen and (max-width: 1024px)': {
                textAlign: 'center',
                fontSize: '24px !important',
            },
        },
    },
    mbContainer: {
        width: 'calc(100vw - 24px)',
        margin: '24px 12px',
        padding: '0 30px',
        '@media screen and (max-width: 768px)': {
            margin: '24px 0px',
            padding: '0',
        },
    },
    dtContainer: {
        maxWidth: '1440px',
        margin: 'auto',
        paddingBottom: '20px',
    },
    error: {
        padding: '20px 30px',
    },
    btnContainer: {
        textAlign: 'center',
        margin: 'auto',
    },
    loadMoreBtn: ({ brandColor }) => ({
        padding: '8px 20px',
        border: `1px solid ${brandColor}`,
        color: brandColor || '#000',
        transition: '0.3s',
        '&:hover': {
            background: brandColor || '#000',
            color: '#fff',
        },
    }),
});

const SubscriptionContainer = ({ userProfile, featureFlags }) => {
    const [page, setPage] = useState(0);
    const customerId = useSelector(getCustomerEmail);
    const brandId = useSelector((state) => getCurrentBrandId(state));
    const brandColor = brandtheme[brandId]?.palette?.colorPrimary;
    const [activeFilter, setActiveFilter] = useState('ACTIVE');
    const underTabDevice = useMediaQuery(`(max-width: ${CUSTOM_BREAKPOINTS.tabLandscape}px)`);
    const classes = styles({ brandColor });

    const isError = useSelector((state) => state[PERSIST_KEY]?.error) || false;
    const errorMessage = useSelector((state) => state[PERSIST_KEY]?.subscriptionResMsg?.replace('{subscription_status}', activeFilter)) || '';
    const isLoading = useSelector((state) => state[PERSIST_KEY]?.fetchingSubscriptions) || false;
    const subscriptions = useSelector((state) => state[PERSIST_KEY]?.subscriptions) || [];
    const nextPage = useSelector((state) => state[PERSIST_KEY]?.nextPage) || false;
    const brand = useSelector(getBrand);
    const dispatch = useDispatch();

    useEffect(() => {
        if (isEmpty(subscriptions)) {
            dispatch(EventDispatcher.handleFetchSubscriptions());
            dispatch(EventDispatcher.fetchSubscriptionByStatus({ customerId, page, status: getSubscriptionStatuses(activeFilter) }, brand, [], featureFlags));
        }
    }, []);

    const handleLoadMore = () => {
        setPage(page + 1);
        dispatch(EventDispatcher.handleFetchSubscriptions());
        dispatch(EventDispatcher.fetchSubscriptionByStatus({ customerId, page: page + 1, status: getSubscriptionStatuses(activeFilter) }, brand, subscriptions, featureFlags));
    };

    const handleFilterChip = (flag) => {
        setPage(0);
        setActiveFilter(flag);
        dispatch(EventDispatcher.handleFetchSubscriptions());
        dispatch(EventDispatcher.fetchSubscriptionByStatus({ customerId, page: 0, status: getSubscriptionStatuses(flag) }, brand, [], featureFlags));
    };

    const handleCancelSubscription = (subscriptionRuleId) => {
        dispatch(EventDispatcher.cancelSubscription(subscriptionRuleId, userProfile.brand, featureFlags));
    };

    const handleUpdateSubscription = (updateType, subscriptionRuleId) => {
        dispatch(EventDispatcher.updateSubscription(subscriptionRuleId, userProfile.brand, featureFlags, updateType));
    };

    const sortedSubscriptions = sortSubscriptions(subscriptions, activeFilter);

    const renderSubscriptionContainer = () => (
        <div className={classes.defaultBody}>
            {underTabDevice && <AccountHome enableHeadingBreakdown disablePageView />}
            <div className={classes.header}>
                <h1>Manage Subscriptions</h1>
            </div>
            <StatusBar
                brandColor={brandColor}
                activeFilter={activeFilter}
                handleFilter={handleFilterChip}
            />
            {!isEmpty(sortedSubscriptions) && (
                <SubscriptionList
                    brandColor={brandColor}
                    customerId={customerId}
                    featureFlags={featureFlags}
                    subscriptions={sortedSubscriptions}
                    handleCancel={handleCancelSubscription}
                    handleUpdate={handleUpdateSubscription}
                />
            )}
            {isLoading && <LoadingSkeleton />}
            {!isLoading && errorMessage && isEmpty(sortedSubscriptions) && (
                <div className={classes.error}>{errorMessage}</div>
            )}
            {nextPage && !isEmpty(sortedSubscriptions) && !isLoading && errorMessage === '' && (
                <div className={classes.btnContainer}>
                    <Button onClick={handleLoadMore} className={classes.loadMoreBtn}>Load More</Button>
                </div>
            )}
        </div>
    );

    if (isError && !isLoading) (<div className={classes.error}>{errorMessage}</div>);

    return (
        <>
            <GraphqlSeoHeadTags seoData={seo} />
            {underTabDevice ? (
                <div className={classes.mbContainer}>
                    {renderSubscriptionContainer()}
                </div>
            ) : (
                <div className={classes.dtContainer}>
                    <Grid className="container" container>
                        <AccountLeftMenu userProfile={userProfile} featureFlags={featureFlags} />
                        <Grid item xs={10} style={{ padding: '40px 11px 0 43px' }}>
                            {renderSubscriptionContainer()}
                        </Grid>
                    </Grid>
                </div>
            )}
        </>
    );
};

SubscriptionContainer.defaultProps = {
    userProfile: {},
    featureFlags: {},
};

SubscriptionContainer.propTypes = {
    userProfile: shape({
        userRole: string.isRequired,
        email: string.isRequired,
        XAtt4: string.isRequired,
        XAtt5: string.isRequired,
    }),
    featureFlags: object,
};

const enhance = compose(
    trackPage({
        type: 'subscription',
        name: 'MyAccount',
        accountPage: true,
    }),
    withPageView({
        pageViewSuccessTest: () => true,
    }),
);

export default enhance(SubscriptionContainer);
